import styled from '@emotion/styled'
import { SPACING } from 'constants/spacing.constants'
import { pxToRem } from 'utils/rem.utils'
import Typography from 'components/core/Typography'
import { ReactNode, useMemo } from 'react'
import { DESIGN_TOKENS } from 'constants/color.constants'
import Row from 'components/core/Row'
import { DynamicChatTimestamp } from 'components/composite/DynamicChatTimestamp'
import { Markdown } from '../Markdown'
import Column from '../Column'

const StyledChatMessage = styled(Row)`
    gap: ${SPACING.p1};

    .chatMessage__body {
        position: relative;

        .chatMessage__body__header {
            letter-spacing: ${pxToRem(0.12)};
        }
    }
`

const StyledMessageBody = styled(Column)<{
    isLeftAligned: boolean
    backgroundColor: string
}>`
    min-width: ${pxToRem(240)};
    max-width: ${pxToRem(400)};
    border-top-left-radius: ${pxToRem(12)};
    border-top-right-radius: ${pxToRem(12)};
    border: 1px solid rgba(11, 43, 64, 0.07);
    background: ${(p) => p.backgroundColor};

    border-bottom-left-radius: ${(p) =>
        p.isLeftAligned ? pxToRem(4) : pxToRem(12)};
    border-bottom-right-radius: ${(p) =>
        p.isLeftAligned ? pxToRem(12) : pxToRem(4)};

    .messageBody__content {
        padding-top: ${SPACING.p1};
        padding-bottom: ${SPACING.p1};
        padding-left: ${SPACING.p2};
        padding-right: ${SPACING.p2};
    }
`

const StyledContent = styled(Markdown)`
    /*
    * Styles below are required to make sure that react-markdown elements render correctly.
    * This is because our app has style resets that end up affecting a lot of primitive components.
    */
    &,
    p {
        color: ${DESIGN_TOKENS.text.foreground};
        font-family: Inter;
        font-style: normal;
        font-weight: 420;
        line-height: ${pxToRem(20)};
        font-size: ${pxToRem(14)};
        word-wrap: break-word;
        overflow-wrap: break-word;
        word-break: break-word;
    }

    ul,
    ol {
        list-style: initial;
        padding-left: ${SPACING.p2};
        padding-bottom: ${SPACING.sm};
    }

    h1,
    h2,
    h3,
    h4 {
        padding-top: ${SPACING.sm};
        padding-bottom: ${SPACING.sm};
        line-height: ${pxToRem(20)};
        font-size: ${pxToRem(16)};
        font-weight: 700;
    }

    a {
        text-decoration: underline;
        font-weight: 500;
    }

    img {
        max-height: ${pxToRem(32)};
        max-width: ${pxToRem(160)};
    }
`

type ChatMessageProps = {
    align: 'left' | 'right'
    header?: string
    content: string
    backgroundColor: string
    createdAt?: string
    className?: string
    footer?: ReactNode
    avatar?: ReactNode
}

export const ChatMessage = ({
    align,
    header,
    content,
    backgroundColor,
    createdAt,
    className,
    footer,
    avatar,
}: ChatMessageProps) => {
    const isLeftAligned = align === 'left'

    const contentToRender = useMemo(
        () => <StyledContent>{content}</StyledContent>,
        [content]
    )

    return (
        <StyledChatMessage className={className}>
            <Column gap="sm" className="chatMessage__body">
                <Row
                    gap="sm"
                    justifyContent={isLeftAligned ? 'flex-start' : 'flex-end'}
                    className="chatMessage__body__header"
                >
                    <Typography isBold variant="caption" color="primary">
                        {header}
                    </Typography>
                    <DynamicChatTimestamp dateTime={createdAt} />
                </Row>
                <StyledMessageBody
                    isLeftAligned={isLeftAligned}
                    backgroundColor={backgroundColor}
                >
                    <div className="messageBody__content">
                        {contentToRender}
                    </div>
                </StyledMessageBody>
                {footer && footer}
            </Column>
            {avatar}
        </StyledChatMessage>
    )
}
